'use client';

import { useAudio } from '@/app/contexts/AudioContext';
import { useCapabilityHoverSwap } from '@/app/playbook/hud/blocks/capability/capabilityHoverSwap';
import { useGlobeHoverSwap } from '@/app/playbook/hud/blocks/globe/globeHoverSwap';
import {
  HOVER_SWAP_FADE_IN_EASING,
  HOVER_SWAP_FADE_MS,
  HOVER_SWAP_FADE_OUT_EASING,
} from '@/app/playbook/hud/blocks/hoverSwapFade';
import { useHudContext } from '@/app/playbook/hud/shared/playbookHUDInfra';
import AudioPlayer from './AudioPlayer';

// Second instance of the audio UI for the scroll experience: mounted inside
// the HUD body (makeBelowDetailHudBody in hud/variants.tsx) as normal flow
// right after the panel stack, whose last element is the labor distribution
// strip — so the bar sits directly below that strip at the HUD's full width.
// Always rendered (even at the top of the page); toggling audio on/off
// collapses/expands it between 0 and auto height. Kept as its own component
// so audio-context ticks (currentTime updates several times a second during
// playback) re-render only this bar, not the memoized HUD.
export function GraphAudioControls() {
  const { isVisible } = useAudio();
  // The lower-half takeover hovers (the capability ladder / takeover-risk
  // explainer, the globe's datacenter list) paint as absolute overlays with
  // no layout height, so a tall one runs past the panel stack and over this
  // bar's slot. The takeover simply COVERS the bar: it drops out on the
  // shared hover-swap clock (opacity + visibility, like every covered swap
  // target) while its layout height stays reserved for the takeover to paint
  // into — no slow half-faded player showing through the detail text.
  // (The below-HUD slot details — power/metric/policy — contribute real
  // layout height, so the bar already sits below those.) Null contexts
  // (outside the providers, or hover swaps disabled) just mean "no takeover".
  const capabilitySwap = useCapabilityHoverSwap();
  const globeSwap = useGlobeHoverSwap();
  const takeoverHoverOpen =
    Boolean(capabilitySwap?.hoveredPoint) ||
    Boolean(capabilitySwap?.hoveredTakeoverRiskArea) ||
    (globeSwap !== null && globeSwap.variant !== undefined && globeSwap.isGlobeHovered);
  // The epilogue takes over the HUD canvas (the panel stack crossfades away
  // via GalaxyHide/epilogueHiddenStyle) — the bar rides the same shared
  // fade so it disappears with the rest of the HUD instead of lingering
  // under the epilogue content.
  const { isEpilogueHudMode, epilogueHiddenStyle } = useHudContext().epilogue;

  return (
    // stopPropagation: the HUD graph box is a metric-details click trigger;
    // interacting with the audio controls shouldn't open that panel.
    // grid-rows 0fr↔1fr animates the collapse/expand to/from auto height.
    // (Chapter tooltips escape this overflow clip by portaling to <body> —
    // see AudioPlayer.)
    <div
      onClick={(event) => event.stopPropagation()}
      className={`relative w-full mt-1 z-[16] grid ${
        isVisible ? 'grid-rows-[1fr]' : 'pointer-events-none grid-rows-[0fr]'
      } ${takeoverHoverOpen ? 'pointer-events-none' : ''}`}
      style={
        isEpilogueHudMode
          ? epilogueHiddenStyle
          : {
              // The audio on/off collapse keeps its slow clock; the takeover
              // cover rides the hover-swap clock, with visibility flipping
              // after the fade-out (and back on immediately) like the other
              // swap targets.
              opacity: takeoverHoverOpen ? 0 : 1,
              visibility: takeoverHoverOpen ? 'hidden' : undefined,
              transition: [
                'grid-template-rows 500ms ease-out',
                `opacity ${HOVER_SWAP_FADE_MS}ms ${takeoverHoverOpen ? HOVER_SWAP_FADE_OUT_EASING : HOVER_SWAP_FADE_IN_EASING}`,
                `visibility 0s ${takeoverHoverOpen ? HOVER_SWAP_FADE_MS : 0}ms`,
              ].join(', '),
            }
      }
    >
      <div className="overflow-hidden">
        <AudioPlayer />
      </div>
    </div>
  );
}
